// src/hooks/routing/usePathTabMapping.ts
import { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

export interface TabRouteConfig {
  tabName: string;
  navPath: string;
}

interface UsePathTabMappingResult {
  currentTab: string;
  handleTabChange: (event: React.SyntheticEvent, value: string) => void;
}

/**
 * Given a list of tab/path route configs (e.g. TAB_ROUTES), derives the
 * active tab from the current URL and returns a change handler that
 * navigates to the corresponding path (or falls back to local tab state
 * if no path match).
 * @param tabRoutes Array of { tabName, navPath } route configs.
 * @param initialTab Fallback tab value when the current path has no mapping.
 */
export function usePathTabMapping(
  tabRoutes: TabRouteConfig[],
  initialTab: string,
): UsePathTabMappingResult {
  const location = useLocation();
  const navigate = useNavigate();

  const pathToTabMap = useMemo(
    () => Object.fromEntries(
      tabRoutes.map(({ navPath, tabName }) => [navPath, tabName]),
    ),
    [tabRoutes],
  );

  const tabToPathMap = useMemo(
    () => Object.fromEntries(
      tabRoutes.map(({ navPath, tabName }) => [tabName, navPath]),
    ),
    [tabRoutes],
  );

  const tabFromPath = pathToTabMap[location.pathname] ?? initialTab;

  const [currentTab, setCurrentTab] = useState<string>(tabFromPath);

  useEffect(() => {
    setCurrentTab(tabFromPath);
  }, [tabFromPath]);

  const handleTabChange = (_event: React.SyntheticEvent, value: string) => {
    const path = tabToPathMap[value];
    if (path && path !== location.pathname) {
      navigate(path);
    } else {
      setCurrentTab(value);
    }
  };

  return { currentTab, handleTabChange };
}